import NavLink from 'next/link';
import {MouseEventHandler, useContext, useMemo} from 'react';

import DuplicateIcon from '@/public/icons/dublicateIcon.svg';
import DuplicateIconWhite from '@/public/icons/dublicateIconWhite.svg';
import NewsletterIcon from '@/public/icons/newsletterIcon.svg';
import DeleteIcon from '@/public/icons/trash-red.svg';
import {useTheme} from '@/src/hooks/useTheme';
import css from '@/src/jobPortal/clinicPositions/styles/ActiveJobPositionsLayout.module.css';
import RouteContext from '@/src/routes/utils/RouteContext';
import TextLocal from '@/src/textLocal/containers/TextLocal';
import {Text} from '@/src/ui';

const TEXT_STATUSES = {
  archived: 'job-portal.archived-at-date',
  expired: 'job-portal.expired-at-date',
};

const ARCHIVED_STATUS = 'archived';

export const ARCHIVED_POSITIONS_ROW_TEST_ID = {
  DELETE: 'ARCHIVED_POSITIONS_ROW_TEST_DELETE',
};

interface ArchivePositionsRowLayoutProps {
  title: string;
  onDeleteClick: MouseEventHandler<HTMLSpanElement>;
  editLink: string;
  archivedAt: string;
  expiredAt: string;
  vacancyApplicationsCount: number;
  id: number;
  isMobile: boolean;
  status: statusType;
}

type statusType = 'archived' | 'expired';

const ArchivePositionsRowLayout = ({
  title,
  onDeleteClick,
  editLink,
  archivedAt,
  vacancyApplicationsCount,
  isMobile,
  expiredAt,
  status,
  id,
}: ArchivePositionsRowLayoutProps) => {
  // @ts-ignore
  const {JOB_PORTAL_MY_POSITION_PREVIEW} = useContext(RouteContext);
  const {isDarkMode} = useTheme();

  const actionsNode = useMemo(() => {
    return (
      <div className={css.actionIcon}>
        <span>
          <NavLink href={editLink}>
            <a>{isDarkMode ? <DuplicateIconWhite /> : <DuplicateIcon />}</a>
          </NavLink>
        </span>

        <span
          onClick={onDeleteClick}
          data-testid={ARCHIVED_POSITIONS_ROW_TEST_ID.DELETE}
        >
          <DeleteIcon />
        </span>
      </div>
    );
  }, [onDeleteClick]);

  return isMobile ? (
    <tr className={css.tableContent}>
      <td>
        <div className={css.positionName}>
          <NavLink href={JOB_PORTAL_MY_POSITION_PREVIEW.getLink(id)}>
            <a>
              <Text
                color={Text.COLOR.GREY}
                fontWeight={Text.FONT_WEIGHT.BOLD}
                lineHeight={Text.LINE_HEIGHT.LINE_HEIGHT_1_6}
              >
                {title}
              </Text>
            </a>
          </NavLink>
        </div>
        <Text
          fontWeight={Text.FONT_WEIGHT.THIN}
          fontSize={Text.FONT_SIZE.SIZE_14}
          color={Text.COLOR.GREY}
        >
          <TextLocal
            id={TEXT_STATUSES[status]}
            params={{
              date: status === ARCHIVED_STATUS ? archivedAt : expiredAt,
            }}
          />
        </Text>
      </td>
      <td>
        <div className={css.newslettersDisabled}>
          <NewsletterIcon />
          <Text
            fontSize={Text.FONT_SIZE.SIZE_14}
            fontWeight={Text.FONT_WEIGHT.BOLD}
            color={Text.COLOR.GREY}
          >
            {vacancyApplicationsCount}
          </Text>
          /
          <Text
            fontSize={Text.FONT_SIZE.SIZE_14}
            fontWeight={Text.FONT_WEIGHT.BOLD}
            color={Text.COLOR.GREY}
          >
            {vacancyApplicationsCount}
          </Text>
        </div>
      </td>
      <td>{actionsNode}</td>
    </tr>
  ) : (
    <tr className={css.tableContent}>
      <td>
        <div className={css.positionName}>
          <NavLink href={JOB_PORTAL_MY_POSITION_PREVIEW.getLink(id)}>
            <a>
              <Text
                color={Text.COLOR.GREY}
                fontWeight={Text.FONT_WEIGHT.BOLD}
                lineHeight={Text.LINE_HEIGHT.LINE_HEIGHT_1_6}
              >
                {title}
              </Text>
            </a>
          </NavLink>
        </div>
        <Text
          fontWeight={Text.FONT_WEIGHT.THIN}
          fontSize={Text.FONT_SIZE.SIZE_14}
          color={Text.COLOR.GREY}
        >
          <TextLocal
            id={TEXT_STATUSES[status]}
            params={{
              date: status === ARCHIVED_STATUS ? archivedAt : expiredAt,
            }}
          />
        </Text>
      </td>
      <td>
        <Text
          fontSize={Text.FONT_SIZE.SIZE_14}
          fontWeight={Text.FONT_WEIGHT.BOLD}
          color={Text.COLOR.GREY}
        >
          {vacancyApplicationsCount}
        </Text>
      </td>
      <td>
        <div className={css.newslettersDisabled}>
          <NewsletterIcon />
          <Text
            fontSize={Text.FONT_SIZE.SIZE_14}
            fontWeight={Text.FONT_WEIGHT.BOLD}
            color={Text.COLOR.GREY}
          >
            {vacancyApplicationsCount}
          </Text>
        </div>
      </td>

      <td>{actionsNode}</td>
    </tr>
  );
};

export default ArchivePositionsRowLayout;
